
import React from "react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { ShoppingBag, Award } from "lucide-react";

const Hero = () => {
  const { t } = useLanguage();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  // Determine which CTA to show based on user state
  const isLoggedIn = !!user;
  const hasLinkedAccount = !!userProfile?.connected_account_id;
  const hasPoints = (userProfile?.points || 0) > 0;

  const handlePrimaryCTA = () => {
    if (!isLoggedIn) {
      // Not logged in -> direct to DartsBeat login
      navigate('/auth');
    } else if (!hasLinkedAccount) {
      // Logged in but not linked -> go to account to link
      navigate('/my-account');
    } else {
      // Logged in and linked -> go to shop to exchange
      navigate('/shop');
    }
  };

  const handleSecondaryCTA = () => {
    if (!isLoggedIn) {
      // Not logged in -> show products to browse
      navigate('/shop');
    } else if (!hasLinkedAccount) {
      // Logged in but not linked -> explore products
      navigate('/shop');
    } else {
      // Linked -> show account/points
      navigate('/my-account');
    }
  };

  // Dynamic CTA text based on user state
  const getPrimaryCTAText = () => {
    if (!isLoggedIn) return t('home.ctaRegister'); // "Entrar com DartsBeat"
    if (!hasLinkedAccount) return t('home.ctaLinkAccount'); // "Ligar Conta DartsBeat"
    return t('home.ctaPrimary'); // "Trocar com Storepoints"
  };

  const getSecondaryCTAText = () => {
    if (!isLoggedIn) return t('home.ctaExplore'); // "Explorar Produtos"
    if (!hasLinkedAccount) return t('home.ctaExplore'); // "Explorar Produtos"
    return t('home.ctaCheckPoints'); // "Ver Meus Storepoints"
  };

  return (
<section className="pt-2 md:pt-8 pb-4 md:pb-10 relative"
         style={{
           backgroundImage: "linear-gradient(to top, rgba(25, 25, 25, 0.9), rgba(25, 25, 25, 0.5)), url('/lovable-uploads/f99c6f04-10df-4776-b2ce-86abdb108010.png')",
           backgroundSize: "cover",
           backgroundPosition: "center",
           backgroundRepeat: "no-repeat",
           height: "auto",
           minHeight: "500px"
         }}>
  <div className="container mx-auto px-4 h-full grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-8">
    <div className="w-full relative flex items-end md:items-center py-4 md:py-0">
      <div className="w-full p-2 md:p-8 lg:p-10 pb-4 md:pb-4">
        <div className="space-y-3 md:space-y-6">
          {/* Brand Title - Mobile Only */}
          <div className="md:hidden animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-red/20 to-pink-600/20 border-2 border-brand-red/40 px-3 py-1.5 rounded-lg backdrop-blur-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse"></div>
              <span className="text-brand-red text-xs font-black uppercase tracking-wide">
                DARTSBEATSTORE
              </span>
            </div>
          </div>

          {/* Value Proposition - Staggered Fade-in Titles */}
          <div className="space-y-2 md:space-y-3">
            <div className="space-y-2 md:space-y-3">
              {/* Step 1 - Fade in first */}
              <div className="flex items-start gap-2 md:gap-4 animate-fade-in animate-delay-200">
                <div className="flex-shrink-0 w-0.5 md:w-1 lg:w-1.5 h-5 md:h-8 lg:h-10 bg-brand-red rounded-full mt-1 md:mt-2"></div>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
                  {t('home.heroTitle1')}
                </h1>
              </div>

              {/* Step 2 - Fade in second with offset */}
              <div className="flex items-start gap-2 md:gap-4 ml-2 md:ml-6 animate-fade-in animate-delay-300">
                <div className="flex-shrink-0 w-0.5 md:w-1 lg:w-1.5 h-5 md:h-8 lg:h-10 bg-brand-red rounded-full mt-1 md:mt-2"></div>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
                  {t('home.heroTitle2')}
                </h1>
              </div>

              {/* Step 3 - Fade in third with more offset */}
              <div className="flex items-start gap-2 md:gap-4 ml-4 md:ml-12 animate-fade-in animate-delay-600">
                <div className="flex-shrink-0 w-0.5 md:w-1 lg:w-1.5 h-5 md:h-8 lg:h-10 bg-brand-red rounded-full mt-1 md:mt-2"></div>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
                  {t('home.heroTitle3')}
                </h1>
              </div>
            </div>
            <h2 className="text-base md:text-2xl lg:text-3xl font-bold text-brand-red leading-tight animate-fade-in animate-delay-700 max-w-3xl">
              {t('home.heroSubtitle')}
            </h2>
            <p className="text-white/90 max-w-3xl text-sm md:text-base lg:text-lg leading-relaxed font-medium animate-fade-in animate-delay-800">
              {t('home.heroDescription')}
            </p>
          </div>

          {/* Dynamic CTAs based on user state */}
          <div className="flex flex-col sm:flex-row gap-2 md:gap-4 pt-1 md:pt-2 animate-slide-in-left animate-delay-400">
            {/* Primary CTA - No icon */}
            <Button
              onClick={handlePrimaryCTA}
              size="lg"
              className="bg-brand-red hover:bg-brand-red/90 text-white font-bold text-xs md:text-lg px-4 md:px-8 py-4 md:py-7 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 hover-glow"
            >
              {getPrimaryCTAText()}
            </Button>

            {/* Secondary CTA - Always ShoppingBag icon */}
            <Button
              onClick={handleSecondaryCTA}
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white/80 text-white hover:bg-white/10 hover:border-white font-bold text-xs md:text-lg px-4 md:px-8 py-4 md:py-7 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <ShoppingBag className="mr-1 md:mr-2 h-4 md:h-5 w-4 md:w-5" />
              {getSecondaryCTAText()}
            </Button>
          </div>

          {/* Points Badge (if user has points) */}
          {hasLinkedAccount && hasPoints && (
            <div className="inline-flex items-center gap-1.5 md:gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 md:px-4 py-1.5 md:py-2 text-white animate-scale-in animate-delay-500">
              <Award className="h-3 md:h-4 w-3 md:w-4 text-yellow-400" />
              <span className="font-bold text-sm md:text-lg">{userProfile?.points || 0}</span>
              <span className="text-xs md:text-sm text-white/80">storepoints</span>
            </div>
          )}
        </div>
      </div>
    </div>
    <div className="hidden md:block w-full relative overflow-hidden rounded-lg h-full animate-slide-in-right animate-delay-200">
      <img
        src="/lovable-uploads/1000070211-removebg-preview-b.png"
        alt="DartsBeat Products"
        className="w-full h-full object-cover rounded-lg animate-float"
      />
    </div>
  </div>
</section>

  );
};

export default Hero;
